import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  CompensationMethod,
  CompensationMethodType,
  DraftReturn,
} from "@redotech/redo-model/draft-return/draft-return";
import { getRelevantDisplayDiscountCodes } from "@redotech/redo-model/draft-return/util";
import { getResource } from "@redotech/redo-model/localization/resource";
import { Payer } from "@redotech/redo-model/return-flow";
import { ExchangesSelection } from "@redotech/redo-model/team";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import ModalCloseButton from "@redotech/redo-web/icon-old/modal-close-button.svg";
import RefundLarge from "@redotech/redo-web/icon-old/refund-large.svg";
import Refund from "@redotech/redo-web/icon-old/refund.svg";
import ShopNowLarge from "@redotech/redo-web/icon-old/shop-now-large.svg";
import ShopNow from "@redotech/redo-web/icon-old/shop-now.svg";
import StoreCreditLarge from "@redotech/redo-web/icon-old/store-credit-large.svg";
import StoreCredit from "@redotech/redo-web/icon-old/store-credit.svg";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Modal, ModalSize } from "@redotech/redo-web/modal";
import { ProductSortKeys } from "@redotech/shopify-client/storefront.graphql";
import {
  ProductInfo,
  ShopifyStorefrontClient,
  getProducts,
} from "@redotech/shopify-storefront";
import { getProductRecommendations } from "@redotech/shopify-storefront/recommendations";
import {
  ShopifyIdType,
  convertShopifyIdToGid,
} from "@redotech/util/shopify-id";
import { Big } from "big.js";
import * as classNames from "classnames";
import { memo, useContext, useMemo, useRef } from "react";
import * as compensationMethodStyles from "./compensation-method.module.css";
import * as productCss from "./components/product.module.css";
import { ExchangeContext } from "./new-items";

export type CompensationMethodSelection =
  | { type: "refund" }
  | { type: "storeCredit" }
  | { type: "exchange"; window?: Window; product?: ProductInfo }
  | { type: "repair" };

/** Filters out same item/variant exchanges that were taken care of earlier */
export function availableMethodsForCompensationMethodsModal(
  compensationMethods: DraftReturn["compensationMethods"],
): DraftReturn["compensationMethods"] {
  return compensationMethods; //.filter(
  //   (method) =>
  //     method.type !== CompensationMethodType.EXCHANGE ||
  //     method.advancedExchangeAvailable,
  // );
}

export const CompensationMethodModal = memo(function CompensationMethodModal({
  draftReturn,
  onCancel,
  onComplete,
  setDraftReturn,
  settings,
}: {
  draftReturn: DraftReturn;
  onCancel: () => void;
  onComplete: (
    method: CompensationMethod,
    exchangeContext: ExchangeContext,
  ) => void;
  setDraftReturn: (draftReturn: DraftReturn) => void;
  settings: ReturnAppSettings;
}) {
  const client = useRequiredContext(RpcClientContext);
  const screenSize = useScreenType();
  const { formatCurrency } = useContext(CurrencyContext);
  const exchangeMethod = draftReturn.compensationMethods.find(
    (method) =>
      method.type === CompensationMethodType.EXCHANGE &&
      method.advancedExchangeAvailable,
  );

  const exchangeAvailable = !!exchangeMethod;

  const copyResource = getResource({
    variant: draftReturn.returnType,
    overrides: settings.resourceOverride,
  });

  const suggestedProductsLoad = useLoad(
    async (signal) => {
      if (
        !exchangeAvailable ||
        !settings.hasStorefrontAccess ||
        settings.theme.hideSuggestedProducts
      ) {
        return null;
      }
      // TODO: STOP BEING SHOPIFY SPECIFIC
      const storefrontClient = new ShopifyStorefrontClient(
        settings.storeUrl,
        settings.storefrontAccessToken,
      );
      return await getRecommendedProducts(
        storefrontClient,
        draftReturn.pendingReturnItems
          .map(
            (pendingItem) =>
              draftReturn.returnableItems
                .find(
                  (returnItem) =>
                    returnItem.id === pendingItem.returnableItemId,
                )
                ?.products.map((product) => product.productId) || [],
          )
          .filter(Boolean)
          .map((id) => `gid://shopify/Product/${id}`) || [],
        3,
      );
    },
    [draftReturn],
  );

  const groupedMethods = useMemo(
    () =>
      draftReturn.compensationMethods.reduce(
        (acc, method) => {
          if (
            method.type === CompensationMethodType.EXCHANGE &&
            !method.advancedExchangeAvailable
          ) {
            return acc;
          }
          if (!acc[method.payer]) {
            acc[method.payer] = [];
          }

          acc[method.payer].push(method);

          return acc;
        },
        {} as Record<Payer, CompensationMethod[]>,
      ),
    [draftReturn],
  );

  const selectionRef = useRef<{
    selection: CompensationMethodSelection;
    window?: Window | null;
  } | null>(null);
  const [setMethodLoad, setMethod] = useTriggerLoad(async (signal) => {
    const { selection, window } = selectionRef.current!;
    try {
      const method = draftReturn.compensationMethods.find(
        (method) => method.type === selection.type,
      );
      if (!method) {
        throw new Error("Method not found");
      }
      const response = await client.setCompensationMethod(
        {
          draftReturnId: draftReturn._id,
          selectedMethod: selection.type as CompensationMethodType,
        },
        { signal },
      );
      setDraftReturn(response.draftReturn);
      onComplete?.(method, {
        window,
        selectedProduct:
          selection.type === "exchange" ? selection.product : undefined,
      });
    } finally {
      selectionRef.current = null;
    }
  });

  const selectMethod = useHandler((selection: CompensationMethodSelection) => {
    selectionRef.current = { selection };
    if (
      selection.type === "exchange" &&
      settings.exchanges.selection === ExchangesSelection.STORE_WEBSITE &&
      !settings.exchanges.shopSiteOneTab
    ) {
      // must happen synchronously to avoid popup blockers
      selectionRef.current!.window = globalThis.open();
    }
    setMethod();
  });

  const loading =
    suggestedProductsLoad.pending ||
    (suggestedProductsLoad.value === undefined &&
      !suggestedProductsLoad.error) ||
    setMethodLoad.pending;

  const availableMethods = availableMethodsForCompensationMethodsModal(
    draftReturn.compensationMethods,
  );

  let optionSize: OptionSize;
  switch (availableMethods.length) {
    case 1:
      optionSize = OptionSize.FULL_WIDTH;
      break;
    case 2:
      optionSize = OptionSize.LONG;
      break;
    default:
      optionSize = OptionSize.COMPACT;
      break;
  }

  const newOrderValue =
    +(draftReturn.newOrderData?.totalPrice || "0") +
    +(draftReturn.newOrderData?.totalTax || "0");
  return (
    <Modal
      onClose={onCancel}
      open
      showHeader={false}
      size={
        screenSize === ScreenType.MOBILE ? ModalSize.MEDIUM : ModalSize.FLEXIBLE
      }
      title=""
    >
      {loading ? (
        <div className={compensationMethodStyles.loadingModal}>
          <LoadingRedoAnimation />
        </div>
      ) : (
        <>
          <Flex className={compensationMethodStyles.modalButtonContainer}>
            <ModalCloseButton
              className={productCss.closeButton}
              onClick={onCancel}
            />
          </Flex>
          <Header
            compensationMethods={draftReturn.compensationMethods}
            discountCodes={getRelevantDisplayDiscountCodes(draftReturn)}
            settings={settings}
          />
          {suggestedProductsLoad.value?.length ? (
            <>
              <div className={compensationMethodStyles.products}>
                {suggestedProductsLoad.value.map((product, i) => (
                  <Product
                    formatCurrency={formatCurrency}
                    key={i}
                    onClick={() => selectMethod({ type: "exchange", product })}
                    product={product}
                    showPriceAfterCredit={
                      !settings.exchanges
                        .exchangeOptionsPreviewShowOriginalPrice
                    }
                    totalExchangeCredit={exchangeMethod!.totalReturnableValue.toNumber()}
                  />
                ))}
              </div>
              <div className={compensationMethodStyles.fullStoreLink}>
                <div
                  className={compensationMethodStyles.link}
                  onClick={() => selectMethod({ type: "exchange" })}
                >
                  SHOP THE FULL STORE {">"}
                </div>
              </div>
            </>
          ) : (
            <div className={compensationMethodStyles.space} />
          )}

          <Flex dir={screenSize === ScreenType.MOBILE ? "column" : "row"}>
            {!!groupedMethods[Payer.REDO]?.length && (
              <div className={compensationMethodStyles.flexContainer}>
                <div
                  className={classNames(
                    compensationMethodStyles.shippingBanner,
                    compensationMethodStyles.covered,
                    groupedMethods[Payer.REDO].length === 1
                      ? compensationMethodStyles.compact
                      : "",
                  )}
                >
                  {settings.hidePortalBranding
                    ? copyResource.FREE_SHIPPING_LABEL_TEXT_NO_BRANDING
                    : `${copyResource.FREE_SHIPPING_LABEL_TEXT} Redo`}
                </div>
                <div className={compensationMethodStyles.rootOptions}>
                  {groupedMethods[Payer.REDO].map((method, i) => (
                    <Option
                      compensationMethod={method}
                      formatCurrency={formatCurrency}
                      key={i}
                      newOrderValue={newOrderValue}
                      onSelect={() => selectMethod({ type: method.type })}
                      optionSize={optionSize}
                      settings={settings}
                    />
                  ))}
                </div>
              </div>
            )}
            {!!groupedMethods[Payer.MERCHANT]?.length && (
              <div className={compensationMethodStyles.flexContainer}>
                <div
                  className={classNames(
                    compensationMethodStyles.shippingBanner,
                    compensationMethodStyles.covered,
                    groupedMethods[Payer.MERCHANT].length === 1
                      ? compensationMethodStyles.compact
                      : "",
                  )}
                >
                  {settings.hidePortalBranding
                    ? copyResource.FREE_SHIPPING_LABEL_TEXT_NO_BRANDING
                    : `${copyResource.FREE_SHIPPING_LABEL_TEXT} ${settings?.theme?.merchantDisplayName ?? settings.merchantName}`}
                </div>
                <div className={compensationMethodStyles.rootOptions}>
                  {groupedMethods[Payer.MERCHANT].map((method, i) => (
                    <Option
                      compensationMethod={method}
                      formatCurrency={formatCurrency}
                      key={i}
                      newOrderValue={newOrderValue}
                      onSelect={() => selectMethod({ type: method.type })}
                      optionSize={optionSize}
                      settings={settings}
                    />
                  ))}
                </div>
              </div>
            )}
            {!!groupedMethods[Payer.CUSTOMER]?.length && (
              <div className={compensationMethodStyles.flexContainer}>
                <div
                  className={classNames(
                    compensationMethodStyles.shippingBanner,
                    compensationMethodStyles.notCovered,
                    groupedMethods[Payer.CUSTOMER].length === 1
                      ? compensationMethodStyles.compact
                      : "",
                  )}
                >
                  {"Pay for "}
                  {settings.theme.shippingFeeText || "shipping fee"}
                </div>
                <div className={compensationMethodStyles.rootOptions}>
                  {groupedMethods[Payer.CUSTOMER].map((method, i) => (
                    <Option
                      compensationMethod={method}
                      formatCurrency={formatCurrency}
                      key={i}
                      newOrderValue={newOrderValue}
                      onSelect={() => selectMethod({ type: method.type })}
                      optionSize={optionSize}
                      settings={settings}
                    />
                  ))}
                </div>
              </div>
            )}
          </Flex>
        </>
      )}
    </Modal>
  );
});

const Header = memo(function Header({
  compensationMethods,
  discountCodes,
  settings,
}: {
  compensationMethods: CompensationMethod[];
  discountCodes: string[];
  settings: ReturnAppSettings;
}) {
  const { formatCurrency } = useContext(CurrencyContext);

  const exchangeMethod = compensationMethods.find(
    (method) => method.type === "exchange" && method.advancedExchangeAvailable,
  );

  if (exchangeMethod) {
    const nonAccruableCredit = Object.values(exchangeMethod.itemValues).reduce(
      (acc, { exchangeCredit }) => acc.plus(exchangeCredit),
      new Big(0),
    );

    const priceAdjustment = Object.values(exchangeMethod.adjustments)
      .reduce((acc, adjustment) => acc.plus(adjustment), new Big(0))
      .plus(nonAccruableCredit)
      .toNumber();

    return (
      <div className={compensationMethodStyles.headerContainer}>
        <div className={compensationMethodStyles.headerText}>
          Shop now with{" "}
          <span className={compensationMethodStyles.shopNowMoney}>
            {formatCurrency(
              exchangeMethod.totalReturnableValue.toNumber() || 0,
            )}
          </span>
          {priceAdjustment > 0 && (
            <PriceAdjustment
              amount={priceAdjustment}
              formatCurrency={formatCurrency}
              returnAdjustmentText={settings.returnAdjustment}
            />
          )}
        </div>
        {showDiscountCodes(settings) && (
          <DiscountCodeSubheader discountCodes={discountCodes} />
        )}
      </div>
    );
  } else {
    return (
      <div className={compensationMethodStyles.headerContainer}>
        <div className={compensationMethodStyles.headerText}>
          Choose your type of refund
        </div>
      </div>
    );
  }
});

const PriceAdjustment = memo(function PriceAdjustment({
  amount,
  formatCurrency,
  returnAdjustmentText,
  hideFeeInCompensationMethodModal = false,
}: {
  amount: number;
  formatCurrency: (amount: number) => string;
  returnAdjustmentText: { positiveName: string; negativeName: string };
  hideFeeInCompensationMethodModal?: boolean;
}) {
  if (amount === 0) {
    return null;
  }
  const isBonus = amount > 0;

  if (hideFeeInCompensationMethodModal && !isBonus) {
    return null;
  }

  return (
    <span
      className={
        isBonus ? compensationMethodStyles.bonus : compensationMethodStyles.fee
      }
    >
      {formatCurrency(Math.abs(amount))}{" "}
      {isBonus
        ? returnAdjustmentText.positiveName
        : returnAdjustmentText.negativeName}
    </span>
  );
});

const DiscountCodeSubheader = memo(function DiscountCodeSubheader({
  discountCodes,
}: {
  discountCodes: string[];
}) {
  const numCodes = discountCodes.length;
  if (numCodes === 0) {
    return null;
  }
  const codes = arrayToString(discountCodes, "and");

  return (
    <div>
      <span>Discount {numCodes === 1 ? "code" : "codes"}</span>{" "}
      <span className={compensationMethodStyles.discountCode}>{codes}</span>{" "}
      <span>will be applied</span>
    </div>
  );
});

const showDiscountCodes = (settings: ReturnAppSettings) => {
  return (
    !settings.theme.hideDiscountCodes && !settings.exchanges.usePreDiscountPrice
  );
};

export const arrayToString = (array: string[], conjunction: string): string => {
  switch (array.length) {
    case 0:
      return "";
    case 1:
      return array[0];
    case 2:
      return `${array[0]} ${conjunction} ${array[1]}`;
    default: {
      let output = array.slice(0, array.length - 1).join(", ");
      output += `, ${conjunction} ${array[array.length - 1]}`;
      return output;
    }
  }
};

export async function getRecommendedProducts(
  storefrontClient: ShopifyStorefrontClient,
  productIDs: string[],
  numRecommendations: number = 3,
): Promise<ProductInfo[]> {
  const originalProducts = new Set(productIDs);
  const recommendations = (
    await Promise.all(
      productIDs.map(async (id) => {
        try {
          return await getProductRecommendations(
            storefrontClient,
            convertShopifyIdToGid(id, ShopifyIdType.Product),
          );
        } catch (e) {
          console.error("Error getting product recommendations", e);
          return [];
        }
      }),
    )
  ).flat();

  const counts = recommendations
    .filter(validProduct)
    .reduce(
      (
        acc: Record<string, { count: number; product: ProductInfo }>,
        product,
      ) => {
        if (!acc[product.id]) {
          acc[product.id] = { count: 0, product };
        }
        acc[product.id].count++;
        return acc;
      },
      {},
    );

  const topRecommendations = Object.entries(counts)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, numRecommendations)
    .map(([_, { product }]) => product);

  try {
    // If we didn't find enough recommendations,
    // fill the rest of the list with the best sellers.
    if (topRecommendations.length < numRecommendations) {
      const iterator = getProducts(storefrontClient, {
        first: numRecommendations,
        query: `-vendor:re\\:do`,
        sortKey: ProductSortKeys.BestSelling,
      });
      for await (const product of iterator) {
        if (topRecommendations.length >= numRecommendations) {
          break;
        }
        // Don't push products we're already recommending,
        // or products we are trying to make recommendations for.
        if (
          validProduct(product) &&
          !counts[product.id] &&
          !originalProducts.has(product.id)
        ) {
          counts[product.id] = { count: 1, product };
          topRecommendations.push(product);
        }
      }
    }
  } catch (e) {
    console.error("Error getting top products", e);
  }
  return topRecommendations;
}

const validProduct = (product: ProductInfo) =>
  product.vendor !== "re:do" && product.variants.nodes?.[0]?.requiresShipping;

const Product = memo(function Product({
  product,
  totalExchangeCredit,
  formatCurrency,
  onClick,
  showPriceAfterCredit,
}: {
  product: ProductInfo;
  onClick: () => void;
  totalExchangeCredit: number;
  formatCurrency: (value: number) => string;
  showPriceAfterCredit: boolean;
}) {
  const originalPrice = product.priceRange.minVariantPrice.amount;
  let priceAfterCredit = originalPrice - totalExchangeCredit;
  if (priceAfterCredit < 0) {
    priceAfterCredit = 0;
  }
  return (
    <div className={compensationMethodStyles.product} onClick={onClick}>
      <img
        className={compensationMethodStyles.productImage}
        src={product.featuredImage?.url}
      />
      <div className={compensationMethodStyles.productText}>
        <div>{stripHtml(product.title)}</div>
        <div className={compensationMethodStyles.productPrices}>
          {showPriceAfterCredit ? (
            <>
              <div className={compensationMethodStyles.productOriginalPrice}>
                {formatCurrency(originalPrice)}
              </div>
              <div>{formatCurrency(priceAfterCredit)}</div>
            </>
          ) : (
            <div>{formatCurrency(originalPrice)}</div>
          )}
        </div>
      </div>
    </div>
  );
});
namespace OptionSize {
  export const LONG = Symbol("long");
  export const COMPACT = Symbol("compact");
  export const FULL_WIDTH = Symbol("full-width");
}

type OptionSize =
  | typeof OptionSize.LONG
  | typeof OptionSize.COMPACT
  | typeof OptionSize.FULL_WIDTH;

const optionSizeClass = {
  [OptionSize.LONG]: compensationMethodStyles.long,
  [OptionSize.COMPACT]: compensationMethodStyles.compact,
  [OptionSize.FULL_WIDTH]: compensationMethodStyles.fullWidth,
};

const Option = memo(function Option({
  settings,
  compensationMethod,
  optionSize,
  onSelect,
  formatCurrency,
  newOrderValue,
}: {
  settings: ReturnAppSettings;
  compensationMethod: CompensationMethod;
  optionSize: OptionSize;
  onSelect: () => void;
  formatCurrency: (amount: number) => string;
  newOrderValue: number;
}) {
  const optionData = {
    exchange: {
      name: settings?.exchangeOptionText || "Shop now",
      description: "to shop now",
      description2: "",
      normal: <ShopNow />,
      large: <ShopNowLarge />,
    },
    storeCredit: {
      name: settings?.storeCreditOptionText || "Store credit",
      normal: <StoreCredit />,
      large: <StoreCreditLarge />,
      description: "to shop later",
      description2: "back to you",
    },
    refund: {
      name: settings?.refundOptionText || "Refund",
      normal: <Refund />,
      large: <RefundLarge />,
      description: "back to you",
      description2: "to shop later",
    },
    repair: {
      name: "Repair",
      // TODO: Think about repair as a return flow end state
      normal: <div />,
      large: <div />,
      description: "to repair",
      description2: "back to you",
    },
  };

  const nonAccruableCredit = Object.values(
    compensationMethod.itemValues,
  ).reduce((acc, { exchangeCredit }) => acc.plus(exchangeCredit), new Big(0));

  const priceAdjustment = Object.values(compensationMethod.adjustments)
    .reduce((acc, adjustment) => acc.plus(adjustment), new Big(0))
    .plus(nonAccruableCredit)
    .toNumber();

  let primaryReturnValue =
    compensationMethod.type === "exchange"
      ? compensationMethod.totalReturnableValue.toNumber()
      : Object.values(compensationMethod.itemValues)
          .reduce(
            (acc, item) =>
              item.isVariantExchange ||
              item.strategy !== compensationMethod.type
                ? acc
                : acc.plus(item.value).plus(item.tax),
            new Big(0),
          )
          .toNumber() + priceAdjustment;

  let secondaryReturnValue =
    compensationMethod.type === "exchange"
      ? 0
      : Object.values(compensationMethod.itemValues)
          .reduce(
            (acc, item) =>
              item.isVariantExchange ||
              item.strategy === compensationMethod.type
                ? acc
                : acc.plus(item.value).plus(item.tax),
            new Big(0),
          )
          .toNumber();

  if (compensationMethod.type === CompensationMethodType.REFUND) {
    primaryReturnValue = primaryReturnValue - newOrderValue;
  } else if (compensationMethod.type === CompensationMethodType.STORE_CREDIT) {
    secondaryReturnValue = secondaryReturnValue - newOrderValue;
    if (secondaryReturnValue < 0) {
      primaryReturnValue = primaryReturnValue + secondaryReturnValue;
      secondaryReturnValue = 0;
    }
  }

  return (
    <div
      className={classNames(
        compensationMethodStyles.option,
        optionSizeClass[optionSize],
      )}
      onClick={onSelect}
    >
      {optionSize === OptionSize.LONG
        ? optionData[compensationMethod.type].large
        : optionData[compensationMethod.type].normal}
      <div
        className={classNames(
          compensationMethodStyles.optionText,
          optionSizeClass[optionSize],
        )}
      >
        <div className={compensationMethodStyles.optionName}>
          <p>{optionData[compensationMethod.type].name}</p>
          <PriceAdjustment
            amount={priceAdjustment}
            formatCurrency={formatCurrency}
            hideFeeInCompensationMethodModal={
              settings.hideFeeInCompensationMethodModal
            }
            returnAdjustmentText={settings.returnAdjustment}
          />
        </div>
        <div className={compensationMethodStyles.optionDescription}>
          {formatCurrency(primaryReturnValue)}{" "}
          {optionData[compensationMethod.type].description}
          {!!secondaryReturnValue && (
            <>
              {" "}
              and
              <div className={compensationMethodStyles.optionDescription}>
                {formatCurrency(secondaryReturnValue)}{" "}
                {optionData[compensationMethod.type].description2}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
});
function stripHtml(html: string) {
  return html.replace(/<[^>]*>?/gm, "");
}
